import { useRef, useCallback } from "react";

const PARTICLE_COUNT = 12;
const OVERLAY_SIZE = 60;

const ButtonAnimation = ({
  children,
  color = "40, 217, 96",
  isActive,
  onAnimate,
  variant = "thumb", // "thumb" | "folder" | "settings"
}) => {
  const canvasRef = useRef(null);
  const ringRef = useRef(null);
  const iconRef = useRef(null);
  const rafRef = useRef(null);

  const spawnParticles = useCallback(
    (opts = {}) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const {
        count = PARTICLE_COUNT,
        gravity = 0.03,
        speed = 1.5,
        sizeMin = 2,
        sizeMax = 2.5,
      } = opts;

      const particles = Array.from({ length: count }, (_, i) => {
        const angle = ((Math.PI * 2) / count) * i + (Math.random() - 0.5) * 0.3;
        return {
          x: cx,
          y: cy,
          vx: Math.cos(angle) * (speed + Math.random() * 1.2),
          vy: Math.sin(angle) * (speed + Math.random() * 1.2),
          size: sizeMin + Math.random() * sizeMax,
          life: 1,
          decay: 0.02 + Math.random() * 0.015,
        };
      });

      const draw = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        let alive = false;
        particles.forEach((p) => {
          p.life -= p.decay;
          if (p.life <= 0) return;
          alive = true;
          p.x += p.vx;
          p.y += p.vy;
          p.vy += gravity;
          const r = Math.max(0, p.size * p.life);
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${color}, ${p.life})`;
          ctx.fill();
        });
        if (alive) rafRef.current = requestAnimationFrame(draw);
      };

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      draw();
    },
    [color],
  );

  const animateRing = useCallback(() => {
    const ring = ringRef.current;
    if (!ring) return;
    let start = null;
    const half = OVERLAY_SIZE / 2;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / 600, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      ring.setAttribute("r", ease * (half - 4));
      ring.setAttribute("opacity", 1 - ease);
      ring.setAttribute("stroke-width", 2.5 * (1 - ease * 0.7));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  const animateThumb = () => {
    const icon = iconRef.current;
    if (!icon) return;
    icon.style.transition =
      "transform 500ms cubic-bezier(0.175, 0.885, 0.32, 1.6)";
    icon.style.transform = "scale(0.4)";
    setTimeout(() => {
      icon.style.transform = "scale(1.25)";
      setTimeout(() => {
        icon.style.transition =
          "transform 250ms cubic-bezier(0.25, 0.1, 0.25, 1)";
        icon.style.transform = "scale(1)";
      }, 200);
    }, 75);
  };

  const animateFolder = () => {
    const icon = iconRef.current;
    if (!icon) return;
    icon.style.transition =
      "transform 500ms cubic-bezier(0.175, 0.885, 0.32, 1.275)";
    icon.style.transformOrigin = "bottom center";
    icon.style.transform = "scaleY(0.6) rotate(-6deg)";
    setTimeout(() => {
      icon.style.transform = "scaleY(1.1) rotate(3deg)";
      setTimeout(() => {
        icon.style.transition =
          "transform 200ms cubic-bezier(0.25, 0.1, 0.25, 1)";
        icon.style.transform = "scaleY(1) rotate(0deg)";
        icon.style.transformOrigin = "center center";
      }, 175);
    }, 100);
  };

  const animateSettings = () => {
    const icon = iconRef.current;
    if (!icon) return;
    icon.style.transformOrigin = "center center";
    icon.style.transition =
      "transform 600ms cubic-bezier(0.175, 0.885, 0.32, 1.275)";
    icon.style.transform = "rotate(120deg) scale(0.7)";
    setTimeout(() => {
      icon.style.transform = "rotate(150deg) scale(1.1)";
      setTimeout(() => {
        icon.style.transition =
          "transform 300ms cubic-bezier(0.25, 0.1, 0.25, 1)";
        icon.style.transform = "rotate(0deg) scale(1)";
      }, 210);
    }, 120);
  };

  const handleClick = () => {
    if (variant === "thumb") {
      if (!isActive) {
        animateThumb();
        animateRing();
        spawnParticles();
      }
    } else if (variant === "folder") {
      animateFolder();
      spawnParticles({
        count: 8,
        gravity: 0.05,
        speed: 1.2,
        sizeMin: 1.5,
        sizeMax: 2,
      });
    } else if (variant === "settings") {
      animateSettings();
      animateRing();
      spawnParticles({
        count: 8,
        gravity: 0,
        speed: 1,
        sizeMin: 1,
        sizeMax: 2,
      });
    }

    onAnimate?.();
  };

  const half = OVERLAY_SIZE / 2;
  const showRing = variant === "thumb" || variant === "settings";

  const overlayStyle = {
    position: "absolute",
    width: OVERLAY_SIZE,
    height: OVERLAY_SIZE,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
  };

  return (
    <div
      onClick={handleClick}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {showRing && (
        <svg width={OVERLAY_SIZE} height={OVERLAY_SIZE} style={overlayStyle}>
          <circle
            ref={ringRef}
            cx={half}
            cy={half}
            r="0"
            fill="none"
            stroke={`rgb(${color})`}
            strokeWidth="2.5"
            opacity="0"
          />
        </svg>
      )}
      <canvas
        ref={canvasRef}
        width={OVERLAY_SIZE}
        height={OVERLAY_SIZE}
        style={overlayStyle}
      />
      <div
        ref={iconRef}
        style={{
          display: "inline-flex",
          transformOrigin: "center center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default ButtonAnimation;
